import type { ApplicationFit } from "@/lib/api/envoy";
import type { PostingFit } from "@/lib/api/resumeService";

/**
 * The fit, at the match and now. The keywords are the skills the
 * posting's text names, found by the catalogue in code; a keyword is
 * covered when the resume says it as a whole word, in any case.
 */

export interface FitView {
    asked: number;
    covered: number;
    /** Covered over asked, for the bar. */
    share: number;
    /** The keywords the resume says, in the order the posting names them. */
    coveredTerms: string[];
    /** The keywords the resume does not say, in the order the posting names them. */
    missing: string[];
    /** Whether the lists were kept. Older matches have the counts alone. */
    listed: boolean;
}

function fitView(
    asked: number,
    covered: number,
    coveredTerms: string[],
    missing: string[],
    listed: boolean,
): FitView {
    const kept = Math.min(Math.max(covered, 0), Math.max(asked, 0));
    return {
        asked,
        covered: kept,
        share: asked > 0 ? kept / asked : 0,
        coveredTerms,
        missing,
        listed,
    };
}

/** The fit envoy scored when it matched. */
export function fitAtMatch(fit: ApplicationFit): FitView {
    return fitView(
        fit.keywords_asked,
        fit.keywords_covered,
        fit.covered ?? [],
        fit.missing ?? [],
        fit.covered !== null && fit.missing !== null,
    );
}

/** The fit of the person's resume as it stands, from the resume service. */
export function fitNow(fit: PostingFit): FitView {
    return fitView(
        fit.asked,
        fit.covered,
        fit.covered_keywords,
        fit.missing.map((keyword) => keyword.term),
        true,
    );
}

/** Said where a posting names no skill, so there is no fit to show. */
export const NO_KEYWORDS_LINE =
    "The posting names none of the skills the catalogue knows, so there is nothing to score.";
